import { router } from "@inertiajs/react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  resetTrialAdminAccountPath,
  updateAgentSettingAdminAccountPath,
} from "@/routes"

// IMP-394: per-archetype free-trial state for an imperative-managed account.
// override is a tri-state: null inherits the partner value, true/false force it.
export interface AccountStandardAgent {
  uid: string
  name: string
  partner_free_trial_enabled: boolean
  override: boolean | null
  effective_free_trial: boolean
  consumed: boolean
  trial_state: "active" | "converted" | "canceled" | null
}

type OverrideValue = "inherit" | "on" | "off"

function overrideValue(override: boolean | null): OverrideValue {
  if (override === null) return "inherit"
  return override ? "on" : "off"
}

const TRIAL_STATE_LABELS: Record<string, string> = {
  active: "Trial in progress",
  converted: "Trial converted",
  canceled: "Trial cancelled",
}

export function AccountTrialTable({
  accountUid,
  agents,
}: {
  accountUid: string
  agents: AccountStandardAgent[]
}) {
  function setOverride(archetypeUid: string, value: OverrideValue) {
    router.patch(
      updateAgentSettingAdminAccountPath({ id: accountUid }),
      { archetype_uid: archetypeUid, override: value },
      {
        preserveScroll: true,
        onError: () => toast.error("Couldn't update the free-trial setting"),
      },
    )
  }

  function resetTrial(archetypeUid: string) {
    if (
      !confirm(
        "Reset this consumed trial? The agent becomes trial-eligible again for this account.",
      )
    ) {
      return
    }
    router.post(
      resetTrialAdminAccountPath({ id: accountUid }),
      { archetype_uid: archetypeUid },
      {
        preserveScroll: true,
        onError: () => toast.error("Couldn't reset the trial"),
      },
    )
  }

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold">Standard agents</h2>
      <p className="text-muted-foreground text-xs">
        Free trial resolves per agent: an account override wins over the
        partner&rsquo;s setting. &ldquo;Inherit&rdquo; follows the partner
        value.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Agent</TableHead>
            <TableHead className="w-48">Free trial</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {agents.map((agent) => (
            <TableRow key={agent.uid}>
              <TableCell className="font-normal">{agent.name}</TableCell>
              <TableCell>
                <Select
                  value={overrideValue(agent.override)}
                  onValueChange={(v) =>
                    setOverride(agent.uid, v as OverrideValue)
                  }
                >
                  <SelectTrigger className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">
                      Inherit ({agent.partner_free_trial_enabled ? "on" : "off"}
                      )
                    </SelectItem>
                    <SelectItem value="on">On</SelectItem>
                    <SelectItem value="off">Off</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                {agent.consumed ? (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm">
                      {agent.trial_state
                        ? TRIAL_STATE_LABELS[agent.trial_state]
                        : "Trial consumed"}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resetTrial(agent.uid)}
                    >
                      Reset
                    </Button>
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">
                    {agent.effective_free_trial ? "Trial-eligible" : "No trial"}
                  </span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
